import {createAction, createReducer} from '@reduxjs/toolkit';

import {
    addHolidayFailure,
    addHolidayRequest,
    addHolidaySuccess,
    deleteHolidayFailure,
    deleteHolidayRequest,
    deleteHolidaySuccess,
    searchHolidaysFailure,
    searchHolidaysRequest,
    searchHolidaysSuccess,
    updateHolidayFailure,
    updateHolidayRequest,
    updateHolidaySuccess,
} from '/src/openapi/ccdb-bff/apis/HolidaysApi';
import {Holiday} from '/src/openapi/ccdb-bff/models/Holiday';
import {HolidaySearchResponse} from '/src/openapi/ccdb-bff/models/HolidaySearchResponse';

/** Action to clear error and success messages (e.g. on navigation away). */
export const clearHolidayMessages = createAction('CCDB_HOLIDAY__CLEAR_MESSAGES');

export type HolidayState = {
    searchResults: HolidaySearchResponse | null,
    loading: boolean,
    saving: boolean,
    deleting: boolean,
    error: string | null,
    successMessage: string | null,
    lastDeletedId: number | null,
    lastSavedHoliday: Holiday | null,
};

const initialState: HolidayState = {
    searchResults: null,
    loading: false,
    saving: false,
    deleting: false,
    error: null,
    successMessage: null,
    lastDeletedId: null,
    lastSavedHoliday: null,
};

export const ccdbHoliday = createReducer<HolidayState>(initialState, builder => builder
    // Clear messages
    .addCase(clearHolidayMessages, state => {
        state.error = null;
        state.successMessage = null;
    })
    // Search Holidays
    .addCase(searchHolidaysRequest, state => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
    })
    .addCase(searchHolidaysSuccess, (state, action) => {
        state.searchResults = action.payload.data;
        state.loading = false;
        state.error = null;
    })
    .addCase(searchHolidaysFailure, (state, action) => {
        state.loading = false;
        state.error = action.payload.data?.message || 'Holiday search failed';
    })
    // Add Holiday
    .addCase(addHolidayRequest, state => {
        state.saving = true;
        state.error = null;
        state.successMessage = null;
    })
    .addCase(addHolidaySuccess, (state, action) => {
        state.saving = false;
        state.lastSavedHoliday = action.payload.data;
        state.successMessage = 'Holiday added successfully.';
    })
    .addCase(addHolidayFailure, (state, action) => {
        state.saving = false;
        state.error = action.payload.data?.message || 'Failed to add holiday';
    })
    // Update Holiday
    .addCase(updateHolidayRequest, state => {
        state.saving = true;
        state.error = null;
        state.successMessage = null;
    })
    .addCase(updateHolidaySuccess, (state, action) => {
        state.saving = false;
        state.lastSavedHoliday = action.payload.data;
        state.successMessage = 'Holiday updated successfully.';
    })
    .addCase(updateHolidayFailure, (state, action) => {
        state.saving = false;
        state.error = action.payload.data?.message || 'Failed to update holiday';
    })
    // Delete Holiday
    .addCase(deleteHolidayRequest, state => {
        state.deleting = true;
        state.error = null;
        state.successMessage = null;
    })
    .addCase(deleteHolidaySuccess, (state, action) => {
        state.deleting = false;
        state.lastDeletedId = action.payload.meta.id;
        state.successMessage = 'Holiday deleted successfully.';
    })
    .addCase(deleteHolidayFailure, (state, action) => {
        state.deleting = false;
        state.error = action.payload.data?.message || 'Failed to delete holiday';
    }),
);
